import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faExpand,
  faCompress
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

export default function Accordion({
  title,
  children,
  variant = "light",
  headerAction,
  openPanel,
  for: useFor,
  panelTriggerKey,
  defaultOpen,
  disabled,
  setOpen,
  highlight,
  onFullscreen,
  isFullscreen,
  showFullscreenButton = false,
  alwaysOpen = false,
  /** Taller body for transport/airline/shipper: more room for thread + composer without clipping Send */
  tallBody = false,
  /** Embedded only: body fills parent height (e.g. email-queries split pane) instead of fixed clamp */
  fillParentHeight = false,
  /** Open: body height follows content; page scrolls. Embedded (e.g. Email Queries) or standalone (e.g. quote partner threads). */
  naturalBodyHeight = false,
  /** Embedded: tighter header (split-pane listings) to match a denser “zoomed out” desktop look */
  embeddedCompact = false,
  /** Brief emerald tint on header bar only (e.g. live SSE message) — no body animation */
  newMessageHeaderFlash = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showError, setShowError] = useState(false);
  const isEmbedded = variant === "embedded";
  const useFillParent = fillParentHeight;
  const useNaturalBody = Boolean(naturalBodyHeight) && !useFillParent;

  useEffect(() => {
    if (title === "Final Quote Cost Table") {
      setIsOpen(defaultOpen);
    }
  }, [defaultOpen]);

  useEffect(() => {
    if (alwaysOpen) {
      setIsOpen(true);
      return;
    }
    if (openPanel && useFor && openPanel[useFor] !== undefined) {
      setIsOpen(true);
    }
    if (openPanel && useFor && openPanel[useFor] === false) {
      setIsOpen(false);
    }
  }, [openPanel, panelTriggerKey, useFor, alwaysOpen]);
  useEffect(() => {
    if (isFullscreen) {
      setIsOpen(true);
    }
  }, [isFullscreen]);


  // useEffect(() => {
  //   if (highlight) {
  //     setPulseAnimation(true);
  //     const timer = setTimeout(() => setPulseAnimation(false), 3000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [highlight]);

  const handleClick = () => {
    if (disabled) {
      // setShake(true);
      // setTimeout(() => setShake(false), 300);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }
    if (alwaysOpen) {
      // Prevent closing if alwaysOpen is true
      return;
    }
    setIsOpen((prev) => {
      const newVal = !prev;
      setOpen && setOpen(newVal);
      return newVal;
    });
  };
  const handleFullscreen = (e) => {
    e.stopPropagation();
    onFullscreen?.();
  };

  const embeddedBorderTone = highlight
    ? "border-blue-200 dark:border-blue-500/30"
    : useNaturalBody
      ? "border-slate-300 shadow-sm dark:border-[oklch(0.4_0.02_264)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.2)]"
      : "border-slate-200/80 dark:border-[oklch(0.3_0.02_264/0.6)]";

  const containerClasses = isEmbedded
    ? `${useNaturalBody ? "overflow-visible" : "overflow-hidden"} w-full min-w-0 max-w-full ${embeddedCompact ? "rounded-[20px]" : "rounded-[24px]"} border bg-white/50 transition-all duration-300 dark:bg-[oklch(0.225_0.026_264/0.94)] ${embeddedBorderTone} ${
      /* h-0 + flex-1: do not let content min-height expand the pane (long email threads) */
      useFillParent ? "flex h-0 min-h-0 flex-1 flex-col " : ""
    }`
    : `${useNaturalBody ? "overflow-visible" : "overflow-hidden"} w-full min-w-0 max-w-full rounded-[28px] border bg-white transition-all duration-300 dark:bg-[oklch(0.225_0.026_264)] ${
      highlight
        ? "border-blue-300 shadow-[0_18px_50px_rgba(37,99,235,0.18)] dark:border-blue-500/30"
        : "border-slate-200 shadow-[0_18px_50px_rgba(15,23,42,0.06)] dark:border-[oklch(0.3_0.02_264/0.6)]"
    } ${useFillParent ? "flex h-0 min-h-0 flex-1 flex-col" : ""}`;

  const headerClasses = isEmbedded
    ? embeddedCompact
      ? `flex shrink-0 items-center justify-between gap-2 rounded-t-[20px] border-b border-slate-200/90 bg-white/65 px-3 py-2 transition-colors duration-150 select-none dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.94)] max-lg:gap-1.5 max-lg:px-2.5 max-lg:py-1.5 ${
          alwaysOpen ? "cursor-default" : "cursor-pointer"
        } ${
          highlight
            ? "border-blue-100 bg-blue-50/70 dark:border-blue-500/20 dark:bg-blue-500/10"
            : newMessageHeaderFlash
              ? "border-emerald-200 bg-emerald-50/90 transition-colors duration-500 ease-out dark:border-emerald-600/40 dark:bg-emerald-950/40"
              : ""
        }`
      : `flex shrink-0 items-center justify-between gap-3 rounded-t-[24px] border-b border-slate-200/90 bg-white/65 px-5 py-4 transition-colors duration-150 select-none dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.94)] max-[1280px]:gap-2 max-[1280px]:px-3 max-[1280px]:py-2.5 max-lg:gap-1.5 max-lg:px-2.5 max-lg:py-2 ${
          alwaysOpen ? "cursor-default" : "cursor-pointer"
        } ${
          highlight
            ? "border-blue-100 bg-blue-50/70 dark:border-blue-500/20 dark:bg-blue-500/10"
            : newMessageHeaderFlash
              ? "border-emerald-200 bg-emerald-50/90 transition-colors duration-500 ease-out dark:border-emerald-600/40 dark:bg-emerald-950/40"
              : ""
        }`
    : `flex items-center justify-between gap-3 border-b px-4 py-3.5 transition-colors duration-150 select-none ${
      alwaysOpen ? "cursor-default" : "cursor-pointer"
    } ${
      highlight
        ? "border-blue-100 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-500/10"
        : newMessageHeaderFlash
          ? "border-emerald-200 bg-emerald-50 transition-colors duration-500 ease-out dark:border-emerald-600/40 dark:bg-emerald-950/35"
          : "border-slate-200 bg-slate-50 dark:border-[oklch(0.3_0.02_264/0.6)] dark:bg-[oklch(0.225_0.026_264/0.6)]"
    }`;

  const bodyClasses = isEmbedded
    ? "flex flex-col text-slate-900 dark:text-slate-200"
    : "flex flex-col bg-white p-2 text-slate-900 dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-200";

  return (
    <>
      <motion.div
        className={containerClasses}
        style={{ fontFamily: "inherit" }}
        aria-expanded={isOpen}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div
          onClick={handleClick}
          className={`${headerClasses} shrink-0`}
          style={{ fontFamily: "inherit" }}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div
              className={`${
                isEmbedded ? (embeddedCompact ? "h-8 w-[3px]" : "h-10 w-[3px] max-lg:h-8") : "h-9 w-1"
              } rounded-full ${
                highlight
                  ? "bg-fk-accent"
                  : newMessageHeaderFlash
                    ? "bg-emerald-500"
                    : "bg-slate-300 dark:bg-[oklch(0.3_0.024_264)]"
              }`}
              aria-hidden="true"
            />
            <div className="min-w-0">
              <span
                className={`block truncate font-semibold text-slate-900 dark:text-slate-100 ${
                  isEmbedded
                    ? embeddedCompact
                      ? "text-sm max-lg:text-[13px]"
                      : "text-base max-[1280px]:text-sm max-lg:text-[13px]"
                    : "text-sm"
                }`}
                style={{
                  fontFamily: "inherit",
                  letterSpacing: "0.1px",
                  fontWeight: 600,
                  lineHeight: "1.3"
                }}
              >
              {title}
              </span>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {highlight && (
                  <motion.span
                    className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
                    initial={{ scale: 0.96, opacity: 0.8 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    New activity
                  </motion.span>
                )}
                {disabled && disabled !== true && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                    Waiting on data
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerAction && (
              <div onClick={(e) => e.stopPropagation()} className="min-w-0 max-w-full shrink-0">
                {headerAction}
              </div>
            )}
            {!alwaysOpen && (
              <div
                className={`transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
              >
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-slate-500 dark:text-slate-400"
                  style={{ fontSize: "12px" }}
                />
              </div>
            )}
            {showFullscreenButton && (
              <button
                onClick={handleFullscreen}
                className={`transition-colors duration-150 ${
                  isEmbedded
                    ? "rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500 hover:bg-slate-100 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
                    : "rounded-full border border-blue-100 bg-blue-50 p-2 text-fk-accent hover:bg-blue-100 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
                }`}
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} style={{ fontSize: "12px" }} />
              </button>
            )}
          </div>
        </div>
        {useFillParent ? (
          isOpen ? (
            <div className="flex h-0 min-h-0 min-w-0 flex-1 flex-col overflow-x-auto overflow-y-hidden">
              <div
                className={`${bodyClasses} h-0 min-h-0 min-w-0 w-full max-w-full flex-1 overflow-x-auto overflow-y-hidden`}
                style={{ fontFamily: "inherit", minHeight: 0 }}
              >
                {children}
              </div>
            </div>
          ) : null
        ) : (
          <motion.div
            initial={false}
            animate={{
              height: isOpen ? (isFullscreen ? "100%" : "auto") : 0,
              opacity: isOpen ? 1 : 0
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            /* Only the inner body scrolls; overflow:auto here creates a second scrollport and can cap scrollRange */
            style={{ overflow: "visible" }}
          >
            {isOpen && (
              <div
                className={`${bodyClasses}${
                  tallBody && !isEmbedded && !useNaturalBody && !useFillParent ? " pb-12" : ""
                }`}
                style={(() => {
                  const base = { fontFamily: "inherit", overflowX: "hidden" };
                  if (isFullscreen) {
                    return {
                      ...base,
                      height: "100%",
                      minHeight: 0,
                      overflowY: useNaturalBody ? "visible" : "auto",
                    };
                  }
                  if (useNaturalBody) {
                    return { ...base, height: "auto", overflowY: "visible" };
                  }
                  /* Fixed height: clamp() made scrollHeight stop short of tall forms; auto + max-height scrolls the full tree */
                  return {
                    ...base,
                    height: "auto",
                    minHeight: 0,
                    maxHeight: isEmbedded
                      ? "min(60rem, 78dvh)"
                      : tallBody
                        ? "min(68rem, 92dvh)"
                        : "min(56rem, 88dvh)",
                    overflowY: "auto",
                  };
                })()}
              >
                {children}
              </div>
            )}
          </motion.div>
        )}
      </motion.div>

      <AnimatePresence>
        {showError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="mt-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300"
            style={{ fontFamily: "inherit", letterSpacing: "0.2px" }}
          >
            Cannot open{(disabled && disabled != true) ? (", as " + disabled) : " yet"}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
